"use client";

import { useState, useEffect, useRef } from "react";
import { ChevronLeft } from "lucide-react";
import { GlobeSimpleIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { FilterNavButton, SearchComposerDesktop, SearchComposerMobile } from "./search-bar";
import AuthModal from "./AuthModal";
import UserMenu from "./UserMenu";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { LanguageDrawer } from "./profile/LanguageDrawer";
import { CartButton } from "./cart/CartButton";
import { Button } from "@/components/ui/button";
import { LOCALE_SHORT_CODES } from "@/i18n/languages";
import { type Locale } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import { useLocale, useTranslations } from "next-intl";

// 로그인 상태는 props 로 받지 않는다 — UserMenu 가 AuthProvider(useAuth)에서 직접 읽는다.
export default function Navbar() {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations("common.search");
  const tCommon = useTranslations("common");
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // Remove locale prefix for path checking
  const pathWithoutLocale = pathname.replace(`/${locale}`, "") || "/";

  // 메인 페이지 전용 분기 (fallback main 페이지들과 분리 — 파급 차단)
  const isHome = pathWithoutLocale === "/";

  // 메인 모바일: 검색 아이콘 ↔ 하단 검색바가 같은 다이얼로그를 공유하도록
  const [searchOpen, setSearchOpen] = useState(false);

  // 메인 모바일: 헤더 지구본 → 프로필과 동일한 언어 바텀시트 재사용
  const [langOpen, setLangOpen] = useState(false);

  // 메인 모바일: 스크롤 시 검색바 → 우상단 돋보기 아이콘 모핑
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    // 비-home 페이지는 isScrolled 를 사용하지 않으므로 리스너 미부착 (stale 값 무해)
    if (!isHome) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        // 검색바(약 48px)가 sticky 헤더 밑으로 사라지는 시점에 돋보기 등장
        setIsScrolled(window.scrollY > 48);
        raf = 0;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isHome]);

  // 헤더 뒤로가기 판정용 — 이 탭에서 "되돌아갈 칸"이 실제로 생겼는지.
  // history.length 의 절대값은 못 믿는다: 외부 사이트에서 들어와도 1 보다 커서
  // router.back() 이 앱 밖으로 나가 버린다. 대신 **마운트 시점 대비 증가분**을 본다 —
  // push 만 length 를 늘리고 replace 는 늘리지 않으므로, 검색 안에서 replace 로
  // 지역만 바꾼 경우(pathname 은 바뀐다)에는 false 로 남아 홈으로 빠진다.
  // (렌더에 영향이 없으므로 state 가 아니라 ref — effect 안 동기 setState 금지 규칙)
  // shouldHide 조기 반환보다 위에 둬야 훅 순서가 안 깨진다.
  const initialHistoryLenRef = useRef(
    typeof window === "undefined" ? 0 : window.history.length,
  );
  const navigatedRef = useRef(false);
  useEffect(() => {
    if (window.history.length > initialHistoryLenRef.current) {
      navigatedRef.current = true;
    }
  }, [pathname]);

  // Hide navbar on auth pages, account settings pages, and messages pages (mobile)
  // `/crew/*` 는 자체 크루 헤더(공개 워드마크 / 콘솔 GNB)를 쓴다. 고객 Navbar 가 겹치면
  // 헤더가 2단이 되고 pt-safe 도 두 번 먹는다.
  const hiddenPaths = ["/login", "/signup", "/auth", "/crew"];
  const isMessagesPage = pathWithoutLocale.startsWith("/messages");
  const shouldHide =
    hiddenPaths.some(
      (path) =>
        pathWithoutLocale === path || pathWithoutLocale.startsWith(path + "/"),
    ) || pathWithoutLocale.startsWith("/account-settings");

  if (shouldHide) {
    return null;
  }

  // Profile pages: hide navbar on mobile via CSS
  const isProfilePage =
    pathWithoutLocale === "/profile" ||
    (pathWithoutLocale.startsWith("/profile/") &&
      !pathWithoutLocale.startsWith("/account-settings"));

  // Reservation detail page: hide navbar on mobile (has its own header)
  const isReservationDetailPage = /^\/reservations\/[^/]+$/.test(
    pathWithoutLocale,
  );

  // Auto-detect variant based on pathname
  const variant =
    pathWithoutLocale === "/"
      ? "main"
      : pathWithoutLocale.startsWith("/s/")
      ? "region"
      : pathWithoutLocale.startsWith("/products/") ||
        pathWithoutLocale.startsWith("/p/") ||
        pathWithoutLocale.startsWith("/partner/")
      ? "product"
      : "main";

  const hideOnMobileClass =
    isProfilePage || isMessagesPage || isReservationDetailPage
      ? "hidden md:block"
      : "";

  // nav 표면(배경/경계선). 홈 모바일만 특수(검색바가 헤더 밑으로 사라지므로 scroll-0 엔
  // 경계선 없이 일체감, 스크롤 시 경계선 추가). 나머지 비-홈(상품/검색결과/일반 main)은
  // 모바일 헤더를 ③ 형태로 통일했으므로 동일하게 흰색 불투명.
  let navSurfaceClass: string;
  if (isHome) {
    navSurfaceClass = `bg-white md:bg-linear-to-b md:from-white md:to-gray-50 md:border-b md:border-gray-200 md:shadow-xs ${
      isScrolled ? "border-b border-gray-200 shadow-xs" : ""
    }`;
  } else if (variant === "region") {
    // 검색결과: 모바일은 nav 바로 아래 고정 필터바(하단 그림자)가 구분선 역할을 하므로
    // nav 테두리를 빼서 2줄 겹침을 방지. 데스크탑은 필터바가 없어 테두리 유지.
    navSurfaceClass = "bg-white md:border-b md:border-gray-200 md:shadow-xs";
  } else {
    navSurfaceClass = "bg-white border-b border-gray-200 shadow-xs";
  }

  // 통합 모바일 헤더 뒤로가기: 이 탭에서 앱 내 이동이 한 번이라도 있었으면 뒤로
  // (검색 페이지는 히스토리 한 칸이라 한 번에 진입 지점으로), 없으면
  // (직접 진입·새로고침) 홈으로 replace.
  // 지점 허브는 시트를 열 때 pathname 은 그대로 두고 쿼리만 pushState 로 쌓으므로
  // (pathname effect 가 안 돈다) 클릭 시점의 history.length 증가분도 함께 본다.
  const goBack = () => {
    const pushedInThisTab =
      navigatedRef.current ||
      window.history.length > initialHistoryLenRef.current;
    if (pushedInThisTab) {
      router.back();
    } else {
      router.replace(`/${locale}`);
    }
  };

  return (
    <>
      <nav
        // data-main-nav: LanguageSuggestBanner 가 헤더 하단 좌표를 실측하는 앵커
        // (BottomNavBar 등 다른 nav 와 구분하기 위한 전용 속성)
        data-main-nav
        className={`sticky top-0 z-50 pt-safe ${navSurfaceClass} ${hideOnMobileClass}`}
      >
        <div
          className={`mx-auto px-4 sm:px-6 lg:px-8 ${
            variant === "main" ? "max-w-480" : "max-w-full"
          }`}
        >
          {/* Desktop Layout (all variants) — 모바일은 variant 별 전용 블록 사용 */}
          <div className="hidden md:flex items-center relative justify-between h-14 md:h-20">
            {/* Logo */}
            <Link
              href={`/${locale}`}
              className="flex items-center gap-2 shrink-0"
            >
              <OptimizedImage
                src="/locarora.svg"
                width={250}
                height={56}
                alt="locarora"
                className="h-6 md:h-10 w-auto"
                loading="eager"
                priority
              />
            </Link>

            {/* Search Bar - Desktop Only (Next to Logo) */}
            {(variant === "main" ||
              variant === "region" ||
              variant === "product") && (
              <div className="hidden md:flex flex-1 max-w-md md:max-w-2xl mx-4">
                {/* 메인만 큰 3칸 검색바, 그 외 페이지는 한 줄 컴팩트(09-28) */}
                <SearchComposerDesktop compact={!isHome} />
              </div>
            )}

            {/* Right Side — desktop only */}
            <div className="hidden md:flex items-center gap-3">
              <CartButton weight="bold" />
              <div className="hidden md:flex items-center gap-3">
                <LanguageSwitcher variant="ghost" showLabel={false} />
                <UserMenu onLoginClick={() => setIsAuthModalOpen(true)} />
              </div>
            </div>
          </div>

          {/* Mobile Layout for Home (/) — 상단줄만 (검색바는 nav 밖 형제로 분리, 스크롤 시 헤더 밑으로 사라짐) */}
          {isHome && (
            <div className="md:hidden flex items-center justify-between h-12">
              <Link href={`/${locale}`} className="flex items-center shrink-0">
                <OptimizedImage
                  src="/locarora.svg"
                  width={250}
                  height={56}
                  alt="locarora"
                  className="h-7 w-auto"
                  loading="eager"
                  priority
                />
              </Link>
              <div className="flex items-center gap-0 shrink-0">
                {/* 돋보기: 장바구니와 동일한 ghost 아이콘 버튼. 자리(폭) 항상 예약 → 장바구니 우측 고정, 스크롤 시 제자리에서 scale */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t("searchButton")}
                  className={cn(
                    "relative h-10 w-10 rounded-full px-0 flex items-center justify-center hover:bg-gray-100 transition-all duration-200 origin-center",
                    isScrolled
                      ? "opacity-100 scale-100"
                      : "opacity-0 scale-50 pointer-events-none",
                  )}
                >
                  <MagnifyingGlassIcon className="size-5" />
                </Button>
                {/* 언어 변경: 데스크탑 LanguageSwitcher 가 md 이상에만 있어 모바일 홈에서만 노출 */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setLangOpen(true)}
                  aria-label={tCommon("languageButton")}
                  className="h-10 rounded-full px-2.5 flex items-center justify-center gap-1 hover:bg-gray-100"
                >
                  <GlobeSimpleIcon className="size-5 shrink-0" />
                  <span className="text-sm font-medium leading-none tracking-wide">
                    {LOCALE_SHORT_CODES[locale as Locale]}
                  </span>
                </Button>
                <CartButton variant="ghost" iconClassName="size-5" />
              </div>
            </div>
          )}

          {/* Mobile Layout — 홈·검색결과 제외 통일(상품/일반 main): 뒤로가기+로고(좌) · 검색아이콘+장바구니(우) */}
          {!isHome && variant !== "region" && (
            <div className="md:hidden flex items-center justify-between h-12">
              <div className="flex items-center gap-1 min-w-0">
                <button
                  onClick={goBack}
                  className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors shrink-0"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <Link
                  href={`/${locale}`}
                  className="flex items-center shrink-0"
                >
                  <OptimizedImage
                    src="/locarora.svg"
                    width={250}
                    height={56}
                    alt="locarora"
                    className="h-7 w-auto"
                    loading="eager"
                    priority
                  />
                </Link>
              </div>
              <div className="flex items-center gap-0 shrink-0">
                <SearchComposerMobile trigger="icon" />
                <CartButton variant="ghost" iconClassName="size-5" />
              </div>
            </div>
          )}

          {/* Mobile Layout for Search Page — 뒤로가기 + 검색바 (검색어가 항상 보이도록 유지) */}
          {variant === "region" && (
            <div className="md:hidden flex items-center gap-2 py-2">
              <button
                onClick={goBack}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors shrink-0"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </button>
              <div className="flex-1 min-w-0">
                <SearchComposerMobile trigger="summary" />
              </div>
              {/* 필터는 검색바 오른쪽(09-28, 에어비앤비 방식) — 검색 페이지가 등록했을 때만 */}
              <FilterNavButton />
            </div>
          )}
        </div>

        {/* Auth Modal */}
        <AuthModal open={isAuthModalOpen} onOpenChange={setIsAuthModalOpen} />
      </nav>

      {/* Home 모바일: 검색바 (비-sticky, 일반 흐름 → 스크롤 시 sticky 헤더 밑으로 자연스럽게 사라짐) */}
      {isHome && (
        // data-home-search-block: LanguageSuggestBanner 실측 앵커 (위 data-main-nav 참고)
        <div data-home-search-block className="md:hidden bg-white">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-480 pt-0.5 pb-2">
            <SearchComposerMobile
              trigger="pill"
              open={searchOpen}
              onOpenChange={setSearchOpen}
            />
          </div>
        </div>
      )}

      {/* 언어 바텀시트: open 이 아니라 isHome 으로 마운트 (닫힘 애니메이션 보존) */}
      {isHome && <LanguageDrawer open={langOpen} onOpenChange={setLangOpen} />}
    </>
  );
}
